import { Link } from 'react-router-dom'
import { ArrowLeft, Brain, Heart, Shield, Users } from 'lucide-react'
import { Logo } from '../components/Logo'

export function Sobre() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="transition-transform duration-300 group-hover:scale-105">
              <Logo size={36} />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-900 font-semibold text-base leading-tight">SecBrain</span>
              <span className="text-teal-600 text-[9px] font-medium tracking-wide uppercase">Neurodivergente</span>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 lg:py-24 px-6 bg-gradient-to-b from-teal-50/50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight mb-6">
            Sobre o <span className="text-gradient">SecBrain</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Nascemos da necessidade real de profissionais de saúde que acompanham
            pacientes neurodivergentes. Nossa missão é conectar famílias e
            profissionais de forma simples e segura.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 lg:py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-6">
                Nossa Missão
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Facilitar o acompanhamento de pacientes neurodivergentes, transformando
                a comunicação fragmentada entre famílias e profissionais em informação
                estruturada e acionável.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Acreditamos que quando famílias conseguem compartilhar o dia a dia de forma
                simples, e profissionais têm acesso a informações organizadas, o cuidado
                se torna mais efetivo e humanizado.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Brain, title: 'Inteligência', desc: 'IA a serviço do cuidado' },
                { icon: Heart, title: 'Empatia', desc: 'Entendemos as necessidades' },
                { icon: Shield, title: 'Segurança', desc: 'Dados sempre protegidos' },
                { icon: Users, title: 'Conexão', desc: 'Famílias e profissionais' },
              ].map((item) => (
                <div key={item.title} className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-teal-100 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-8 text-center">
            Nossa História
          </h2>
          <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
            <p>
              O SecBrain nasceu da experiência prática de profissionais de saúde que
              acompanham crianças e adolescentes com TEA e outras condições neurodivergentes.
            </p>
            <p>
              Percebemos que uma das maiores dificuldades era a fragmentação da informação:
              famílias enviavam atualizações por WhatsApp, e-mail e outros canais, e os
              profissionais passavam horas organizando essas informações manualmente.
            </p>
            <p>
              Criamos uma solução que usa o canal que as famílias já conhecem — o WhatsApp —
              e aplica inteligência artificial para organizar, categorizar e destacar o que
              é mais importante para cada paciente.
            </p>
            <p>
              Hoje, centenas de profissionais usam o SecBrain para acompanhar milhares de
              pacientes, economizando tempo e melhorando a qualidade do cuidado.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-12 text-center">
            Nossos Valores
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Privacidade em Primeiro Lugar',
                desc: 'Dados de saúde são sensíveis. Tratamos cada informação com o máximo cuidado, seguindo rigorosamente a LGPD e as melhores práticas de segurança.',
              },
              {
                title: 'Tecnologia com Propósito',
                desc: 'A IA não substitui profissionais — ela os potencializa. Cada funcionalidade é pensada para facilitar o trabalho humano, não para substituí-lo.',
              },
              {
                title: 'Acessibilidade',
                desc: 'Famílias não precisam baixar apps ou fazer cadastros complexos. Usamos o que elas já conhecem: o WhatsApp.',
              },
            ].map((value) => (
              <div key={value.title} className="text-center">
                <h3 className="font-semibold text-gray-900 text-lg mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 px-6 bg-teal-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-6">
            Quer conhecer mais?
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Agende uma demonstração e descubra como o SecBrain pode ajudar no
            acompanhamento dos seus pacientes.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-colors"
          >
            Voltar para o site
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} SecBrain Neurodivergente. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
